import { Component, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../core/auth/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { navBarConfig } from './config/nav-bar-config';
import { NavBarItem } from './models/nav-bar-item.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import * as HubSelectors from '../store/hub.selector';
import { SvgIconComponent } from '../shared/ui/svg-icon/svg-icon.component';
import { AsyncPipe } from '@angular/common';
import { UserPanelComponent } from '../shared/ui/user-panel/user-panel.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SvgIconComponent, UserPanelComponent, AsyncPipe]
})
export class SidebarComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);

  navItems: NavBarItem[] = navBarConfig().navItems;

  public showMobileNavigation: boolean = false;
  activeMenu: string | null = null;

  destroyRef = inject(DestroyRef);

  hubTheme$ = this.store.select(HubSelectors.getHubTheme);

  constructor() {
    //without this sometimes the active menu is not set on load
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActiveMenuOnLoad();
      }
    });
  }

  setActiveMenuOnLoad(): void {
    // onload if a child is active, set the parent as active
    const parentItem = navBarConfig().navItems.find((item) =>
      item.subItems?.some((sub) => this.router.url.startsWith(sub.path))
    );

    if (parentItem) {
      this.activeMenu = parentItem.path;
    }
  }

  public logout() {
    this.auth.logout();
  }

  public routeTo(item: NavBarItem) {
    this.showMobileNavigation = false;

    // An external item is not a route. Nessy Explorer is served from the
    // same origin but is its own page, so the router would not find it and the ** fallback would
    // quietly send the user to Account Search instead.
    if (item.isExternal) {
      window.location.assign(item.path);

      return;
    }

    this.router.navigateByUrl(item.path);

    // Check if the clicked route is not a submenu item
    const isSubmenuItem = navBarConfig().navItems.some((parent) =>
      parent.subItems?.some((sub) => sub.path === item.path)
    );

    if (!isSubmenuItem) {
      this.activeMenu = null;
    }
  }

  public toggleSidebar() {
    this.showMobileNavigation = !this.showMobileNavigation;
  }

  // Handle navigation and submenu toggle
  toggleMenu(path: string): void {
    if (this.activeMenu === path) {
      this.activeMenu = null;
    } else {
      this.activeMenu = path;
    }
  }

  public isActive(route: string): boolean {
    if (route === '/') {
      return this.router.url === '/'; // Only activate Dashboard for exact '/'
    }
    return this.router.url.startsWith(route);
  }
}
